import React, { FC, useState } from 'react';
import { useDataProvider } from 'src/app/common/utils';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { ParamsProps } from '../../../../../common/components/ParamsProvider';
import { getDayEnd, getFirstDayOfMonth, getDayStart, getLastDayOfMonth } from '../../../../../common/utils/time-utils';
import PruTable from '../../../../../common/components/PruTable/PruTable';
import PruFilter, { PruFilterItemType } from '../../../../../common/components/PruTable/PruFilter';
import { PaginateListMore } from 'src/app/common/types/common-types';
import { CandidateSurveyReportListParams } from '../../../types/candidateSurveyReportTypes';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import moment from 'moment';
import {
  fetchCandidateLifeStyleSurveyDetail,
  fetchCandidateLifeStyleSurveyDetailDownload,
} from '../../../network/candidateLifeStyleSurveyDetailReportCrud';
import { LifeStyleSurveyDetailReportListParams } from '../../../types/candidateLifeStyleSurveyDetailReportTypes';

type CandidateLifeStyleSurveyDetailReportProps = {} & ParamsProps;

const initialState: CandidateSurveyReportListParams = {
  requestedDateFrom: getDayStart(new Date()),
  requestedDateTo: getDayEnd(new Date()),
  agentCode: '',
  officeCode: '',
  surveyName: '',
  territory: '',
  // selectedColumns: [],
  page: 1,
  limit: 5,
};

const paramsInitiator = (initialParams?: Record<string, string>): CandidateSurveyReportListParams => {
  return initialParams
    ? {
        requestedDateFrom: initialParams.requestedDateFrom
          ? getDayStart(new Date(initialParams.requestedDateFrom))
          : getFirstDayOfMonth(new Date()),
        requestedDateTo: initialParams.requestedDateTo
          ? getLastDayOfMonth(new Date(initialParams.requestedDateTo))
          : getLastDayOfMonth(new Date()),
        agentCode: '',
        officeCode: '',
        surveyName: '',
        territory: '',
        // selectedColumns: [],
        page: 1,
        limit: 5,
      }
    : initialState;
};

const columns: any = [
  {
    keyIndex: 'agentCode',
    align: 'center',
    displayName: 'AG Code',
    renderData: (row: any) => row.agentCode || '-',
  },
  {
    keyIndex: 'fullname',
    align: 'center',
    displayName: 'AG Name',
    renderData: (row: any) => row?.agentInfo?.fullname || '-',
  },
  {
    keyIndex: 'roleCode',
    align: 'center',
    displayName: 'AG Role',
    renderData: (row: any) => row?.agentInfo?.designation?.name || '-',
  },
  {
    keyIndex: 'officeCode',
    align: 'center',
    displayName: 'Office Code',
    renderData: (row: any) => row?.agentInfo?.division?.code || '-',
  },
  {
    keyIndex: 'officeName',
    align: 'center',
    displayName: 'Office Name',
    renderData: (row: any) => row?.agentInfo?.division?.description || '-',
  },
  {
    keyIndex: 'region',
    align: 'center',
    displayName: 'Region',
    renderData: (row: any) => row?.agentInfo?.region || '-',
  },
  {
    keyIndex: 'saleZone',
    align: 'center',
    displayName: 'Territory',
    renderData: (row: any) => row?.agentInfo?.zone || '-',
  },
  {
    keyIndex: 'branchCode',
    align: 'center',
    displayName: 'Branch Code',
    renderData: (row: any) => row?.agentInfo?.branch || '-',
  },

  {
    keyIndex: 'unitCode',
    align: 'center',
    displayName: 'Unit Code',
    renderData: (row: any) => row?.agentInfo?.unit || '-',
  },

  {
    keyIndex: 'surveyName',
    align: 'center',
    displayName: 'Survey Name',
    renderData: (row: any) => row?.surveyName || '-',
  },

  {
    keyIndex: 'candidateName',
    align: 'center',
    displayName: 'Candidate Name',
    renderData: (row: any) => row?.candidateName || '-',
  },
  {
    keyIndex: 'candidatePhone',
    align: 'center',
    displayName: 'Candidate Phone',
    renderData: (row: any) => row?.candidatePhone || '-',
  },

  {
    keyIndex: 'responsedDate',
    align: 'center',
    displayName: 'Responsed Date',
    renderData: (row: any) => (row.submittedAt ? moment(row.submittedAt).format('DD/MM/YYYY') : '-'),
  },
  {
    keyIndex: 'responsedTime',
    align: 'center',
    displayName: 'Responsed Time',
    renderData: (row: any) => (row.submittedAt ? moment(row.submittedAt).format('HH:mm:ss') : '-'),
  },
];
const LifeStyleSurveyDetailReport: FC<CandidateLifeStyleSurveyDetailReportProps> = ({ initialParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();
  const defaultFilterValue = paramsInitiator(initialParams);

  const [filterState, setFilterState] = useState<LifeStyleSurveyDetailReportListParams>(defaultFilterValue);
  const [reportList, setReportList] = useState<PaginateListMore<any>>();
  const { isLoading, refreshData } = useDataProvider<PaginateListMore<any>>(
    async () => {
      try {
        if (filterState.requestedDateFrom && filterState.requestedDateTo) {
          const momentDateFrom = moment(filterState.requestedDateFrom);
          const momentDateTo = moment(filterState.requestedDateTo);

          if (momentDateTo.isBefore(momentDateFrom, 'day')) {
            dispatch(
              appendAlertItem([
                {
                  severity: AlertType.ERROR,
                  title: 'Error',
                  content: 'End date must be after Begin date',
                },
              ]),
            );
            return;
          }

          if (Number(momentDateTo.diff(momentDateFrom, 'months', true).toFixed(1)) > 3) {
            dispatch(
              appendAlertItem([
                {
                  severity: AlertType.ERROR,
                  title: 'Error',
                  content: 'Searching period must be less than or equal to 3 months depending on FROM MONTH',
                },
              ]),
            );
            return;
          }
        }

        return await fetchCandidateLifeStyleSurveyDetail(filterState, dispatch);
      } catch (err) {}
    },
    setReportList,
    false,
  );

  const fetchCsvtDownload = () => {
    try {
      fetchCandidateLifeStyleSurveyDetailDownload(filterState, dispatch);
    } catch (err) {}
  };

  return (
    <>
      <PruFilter
        title={Translation('report.candidate.lifestyleSurveyDetail.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'agentCode',
            initialValue: filterState.agentCode,
            displayName: Translation('report.candidate.lifestyleSurveyDetail.agentCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'officeCode',
            initialValue: filterState.officeCode,
            displayName: Translation('report.candidate.lifestyleSurveyDetail.officeCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'surveyName',
            initialValue: filterState.surveyName,
            displayName: Translation('report.candidate.lifestyleSurveyDetail.surveyName'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'territory',
            initialValue: filterState.territory,
            displayName: Translation('report.candidate.lifestyleSurveyDetail.territory'),
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'requestedDateFrom',
            fieldTo: 'requestedDateTo',
            initialDateFrom: filterState.requestedDateFrom,
            initialDateTo: filterState.requestedDateTo,
            defaultDateFrom: defaultFilterValue.requestedDateFrom,
            defaultDateTo: defaultFilterValue.requestedDateTo,
            displayName: Translation('report.candidate.lifestyleSurveyDetail.periodDate'),
            required: true,
          },
        ]}
        onChangeFilter={(data) => {
          setFilterState({
            ...filterState,
            agentCode: data?.agentCode.trim(),
            officeCode: data?.officeCode.trim(),
            surveyName: data?.surveyName.trim(),
            requestedDateFrom: data.requestedDateFrom,
            requestedDateTo: data.requestedDateTo,
            territory: data?.territory.trim(),
          });
        }}
        fetchData={refreshData}
      />
      <PruTable
        disableBulkSelect
        dataSource={reportList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('report.candidate.lifestyleSurveyDetail.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('report.common.export'),
            onClick: () => fetchCsvtDownload(),
          },
        ]}
        // selectedColumnsDef={{
        //   columns: columns.map((item: any) => ({
        //     key: item.keyIndex,
        //     value: item.displayName,
        //   })),
        //   onChange: (columns) => {
        //     setFilterState({
        //       ...filterState,
        //       selectedColumns: columns,
        //     });
        //   },
        // }}
        operationDef={[]}
        columnDef={columns}
        totalPages={reportList?.totalPages}
        totalRecords={reportList?.totalRecords}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default LifeStyleSurveyDetailReport;
